import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Params, useParams } from 'react-router-dom';

import {
  type ListTransfers as ApiListTransfers,
  type TransferBatch as ApiTransferBatch,
  type UnifiedTransfer,
  type UpdateTransferBatchRequest,
  transferBatchGetTransferBatch,
  transferBatchGetTransferBatchQueryKey,
  transferBatchUpdateTransferBatch,
  transferSearchTransfers,
  transferSearchTransfersQueryKey,
} from '@column/api';
import { Dropdown, Icon, Input } from '@column/column-ui-kit';
import { Badge, Button, DataTable, Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { BankAccountCard, Loading, Pagination, RenderFields, SectionHeader } from '~/components';
import { LogoLoading } from '~/elements';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { transferTableColumns } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { TransferBatchHeader } from './_components/TransferBatchHeader';

export const PageTransferBatchesView: React.FC = () => {
  const { transferBatchId } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('transfers');
  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, setPageSize, ...pagination } = usePaginationState({
    defaultPageSize: pageSize,
  });
  const [entriesOpen, setEntriesOpen] = React.useState(false);

  const queryClient = useQueryClient();
  const { data: transferBatch, isLoading: isTransferBatchLoading } = useQuery<ApiTransferBatch>({
    queryKey: transferBatchGetTransferBatchQueryKey({
      path: { transfer_batch_id: transferBatchId ?? '' },
    }),
    queryFn: async () => {
      const { data } = await transferBatchGetTransferBatch({
        path: { transfer_batch_id: transferBatchId ?? '' },
        throwOnError: true,
      });
      return data as ApiTransferBatch;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
  });

  useBreadcrumbTitle(transferBatch?.description);

  const { mutate: updateTransferBatch, isPending: isUpdateTransferBatchPending } = useMutation<
    ApiTransferBatch,
    Error,
    UpdateTransferBatchRequest
  >({
    mutationFn: async (request) => {
      const { data } = await transferBatchUpdateTransferBatch({
        path: { transfer_batch_id: transferBatchId ?? '' },
        body: request,
        throwOnError: true,
      });
      return data as ApiTransferBatch;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        transferBatchGetTransferBatchQueryKey({
          path: { transfer_batch_id: transferBatchId ?? '' },
        }),
        data
      );
      addSuccessNotification({
        content: 'Transfer batch updated',
      });
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const { data: transfersData, isLoading: isTransfersLoading } = useQuery<ApiListTransfers>({
    queryKey: transferSearchTransfersQueryKey({
      query: {
        limit: pagination.pageSize,
        starting_after: cursor,
        transfer_batch_id: transferBatchId ?? '',
      },
    }),
    queryFn: async () => {
      const { data } = await transferSearchTransfers({
        query: {
          limit: pagination.pageSize,
          starting_after: cursor,
          transfer_batch_id: transferBatchId ?? '',
        },
        throwOnError: true,
      });
      return data as ApiListTransfers;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const formMethods = useForm<UpdateTransferBatchRequest>({
    defaultValues: {
      description: transferBatch?.description ?? '',
    },
  });
  useEffect(() => {
    formMethods.setValue('description', transferBatch?.description ?? '');
  }, [transferBatch?.description, transferBatch?.id]);

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, updateTableConfig]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({
        timeFormat,
        onTimeFormatChange: handleTimeFormatChange,
      }),
    [handleTimeFormatChange, timeFormat]
  );

  const transfers = useMemo(() => transfersData?.transfers ?? [], [transfersData]);
  const tableMinHeight = transfers.length === 0 ? 341 : undefined;
  const isTableInitialLoading = isTransfersLoading && transfers.length === 0;

  const handleNext = () => {
    if (!transfersData || transfersData.transfers.length === 0) return;
    onNext({ cursor: transfersData.transfers[transfersData.transfers.length - 1].id });
  };

  return (
    <>
      <Loading isLoading={isTransferBatchLoading} fullHeight>
        {transferBatch && (
          <TransferBatchHeader
            amount={transferBatch.total_amount}
            createdAt={transferBatch.created_at}
            currencyCode={transferBatch.total_currency}
            transferBatchId={transferBatch.id}
            transferCount={transferBatch.transfer_count}
          />
        )}
        <SectionHeader text="Batch Details" border>
          <Button
            size="sm"
            disabled={isUpdateTransferBatchPending}
            onClick={formMethods.handleSubmit(
              (data) => {
                updateTransferBatch(data);
              },
              (errors) => handleFormErrors({ errors })
            )}
          >
            {isUpdateTransferBatchPending ? 'Saving...' : 'Save'}
          </Button>
        </SectionHeader>
        <Inner pt={0} px="var(--page-margin)">
          <form>
            <FormProvider {...formMethods}>
              <RenderFields
                sections={[
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ onBlur, onChange, value }, { isTouched, error }) => (
                          <Input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={String(value)}
                            hasError={isTouched && !!error}
                          />
                        ),
                      },
                      {
                        id: 'bankAccount',
                        label: 'Bank Account',
                        children: () =>
                          transferBatch?.bank_account_id ? (
                            <BankAccountCard bankAccountId={transferBatch.bank_account_id} />
                          ) : null,
                      },
                    ],
                  },
                ]}
              />
            </FormProvider>
          </form>
        </Inner>
        <SectionHeader text="Transfers" border />
        <Loading isLoading={isTableInitialLoading}>
          <Inner pt={0} px="var(--page-margin)">
            <DataTable<UnifiedTransfer>
              columns={columns}
              data={transfers}
              isLoading={false}
              loadingContent={<LogoLoading />}
              style={{ minHeight: tableMinHeight }}
              emptyContent={
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.AnimationArrows />
                    </EmptyMedia>
                    <EmptyTitle>No transfers found</EmptyTitle>
                    <EmptyDescription>This batch does not have any transfers yet.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              }
              rowClassName="min-h-11"
              rowKey={(row) => row.id}
              onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
            />

            <div className="relative z-[1] flex items-center justify-between pt-4">
              <Dropdown
                options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                  label: entries.toString(),
                  value: entries,
                }))}
                size="small"
                variant="muted"
                active={pagination.pageSize}
                customLabel={
                  <Badge
                    variant="secondary"
                    className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                  >
                    {pagination.pageSize}
                    {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                  </Badge>
                }
                onOpenChange={setEntriesOpen}
                onChange={setPageSize}
              />
              <Pagination
                current={pageIndex + 1}
                disablePrev={!hasPrevious}
                disableNext={!transfersData?.has_more}
                onPrev={onPrevious}
                onNext={handleNext}
              />
            </div>
          </Inner>
        </Loading>
      </Loading>
    </>
  );
};
